/**
 * Merchant Color Utilities
 *
 * Handles conversion of merchant color codes (hex) to Tailwind-compatible classes
 * color1: Main/primary color (backgrounds, buttons, headers)
 * color2: Secondary color (spinners, small accents)
 */

/**
 * Get primary color (color1) for main elements
 */
export function getMerchantPrimaryColor(merchant) {
  if (!merchant?.color1) {
    return 'text-rose-500';
  }

  return 'merchant-primary';
}

/**
 * Get secondary color (color2) for spinners and small elements
 */
export function getMerchantSecondaryColor(merchant) {
  if (!merchant?.color2) {
    return 'text-pink-600';
  }

  return 'merchant-secondary';
}

/**
 * Inject merchant colors as CSS variables
 * Call this once at app initialization
 */
export function injectMerchantColors(merchant) {
  if (!merchant?.color1 || !merchant?.color2) {
    return; // Use default CSS
  }

  // Create or update style tag
  let styleTag = document.getElementById('merchant-colors');
  if (!styleTag) {
    styleTag = document.createElement('style');
    styleTag.id = 'merchant-colors';
    document.head.appendChild(styleTag);
  }

  // Inject CSS with merchant colors - using solid colors, no gradients
  styleTag.textContent = `
    :root {
      --merchant-color-1: ${merchant.color1};
      --merchant-color-2: ${merchant.color2};
    }

    .merchant-primary {
      color: var(--merchant-color-1);
    }

    .merchant-secondary {
      color: var(--merchant-color-2);
    }

    .merchant-bg {
      background-color: var(--merchant-color-1);
    }

    .merchant-bg-hover:hover {
      background-color: color-mix(in srgb, var(--merchant-color-1) 85%, black);
    }

    .merchant-border {
      border-color: var(--merchant-color-1);
    }

    .merchant-text {
      color: var(--merchant-color-1);
    }

    /* Lighter version for backgrounds */
    .merchant-bg-light {
      background-color: color-mix(in srgb, var(--merchant-color-1) 10%, white);
    }

    .merchant-border-light {
      border-color: color-mix(in srgb, var(--merchant-color-1) 30%, white);
    }
  `;
}

/**
 * Get button classes with merchant colors (solid color1)
 */
export function getMerchantButtonClasses(merchant) {
  if (!merchant?.color1) {
    return `bg-rose-500 text-white hover:bg-rose-600 transition-colors`;
  }

  return `merchant-bg merchant-bg-hover text-white transition-colors`;
}

/**
 * Get header classes with merchant solid color (color1)
 */
export function getMerchantHeaderClasses(merchant) {
  if (!merchant?.color1) {
    return `bg-rose-500 text-white`;
  }

  return `merchant-bg text-white`;
}

export default {
  getMerchantPrimaryColor,
  getMerchantSecondaryColor,
  getMerchantButtonClasses,
  getMerchantHeaderClasses,
  injectMerchantColors
};
